import { useContext } from 'react';
import { AuthenticationContext, AuthContextValue } from '../contexts/AuthenticationContext';

/**
 * Custom hook to access authentication context
 *
 * Provides convenient access to authentication state and methods
 * throughout the application
 *
 * Requirements:
 * - 7.4: Provide access to authentication context
 *
 * @returns Authentication context value with state and methods
 * @throws Error if used outside of AuthenticationProvider
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { authState, login, logout } = useAuth();
 *
 *   if (authState.loading) {
 *     return <div>Loading...</div>;
 *   }
 *
 *   if (!authState.isAuthenticated) {
 *     return <button onClick={login}>Login</button>;
 *   }
 *
 *   return <div>Welcome, {authState.email}!</div>;
 * }
 * ```
 */
export function useAuth(): AuthContextValue {
  const context = useContext(AuthenticationContext);

  if (!context) {
    throw new Error(
      'useAuth must be used within an AuthenticationProvider. ' +
        'Wrap your component tree with <AuthenticationProvider>.',
    );
  }

  return context;
}
